// ============================================================================
// REVIEW BOARD VIEW - Streamlined Facet Review and Entity Mapping
// Features: Pre-selected facets, many-to-one mapping, drag-drop grouping
// ============================================================================

import { useState, useEffect, useMemo, useCallback } from 'react'
import { useSessionStore } from '@/stores/sessionStore'
import {
  sourcesApi,
  type SourceWithFacets,
  type DetectedFacet,
  type FacetEntityGroupWithMembers,
} from '@/lib/api/client'
import { FACET_MAP } from '@/lib/analyzer/facetSchema'
import { FACET_TYPE_CONFIG, FACET_CATEGORY_CONFIG, type FacetCategory, type FacetType } from '@/types/interviewer'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import {
  AlertCircle,
  Activity,
  Target,
  TrendingUp,
  DollarSign,
  Clock,
  AlertTriangle,
  CheckSquare,
  GitBranch,
  Lightbulb,
  Lock,
  Link as LinkIcon,
  HelpCircle,
  CheckCircle,
  MessageCircle,
  FileText,
  ArrowLeft,
  Plus,
  Loader2,
  ChevronDown,
  ChevronRight,
  X,
  Sparkles,
  Briefcase,
  BarChart,
  RefreshCw,
  GripVertical,
  Trash2,
  Check,
  Users,
  Layers,
  type LucideIcon,
} from 'lucide-react'

// ============================================================================
// TYPES
// ============================================================================

interface FacetSelection {
  id: string
  selected: boolean
  groupId?: string  // If part of a group
}

interface DraftGroup {
  id: string
  entityLabel: string
  entityType: string
  entityDescription: string
  facetIds: string[]
  primaryFacetId?: string
}

// ============================================================================
// CATEGORY ICON MAPPING
// ============================================================================

const CATEGORY_ICONS: Record<FacetCategory, LucideIcon> = {
  market: Target,
  business: Briefcase,
  solution: Lightbulb,
  insights: MessageCircle,
}

const ICON_MAP: Record<string, LucideIcon> = {
  AlertCircle,
  Activity,
  RefreshCw,
  DollarSign,
  Clock,
  AlertTriangle,
  CheckSquare,
  GitBranch,
  Lightbulb,
  Lock,
  Users,
  Link: LinkIcon,
  HelpCircle,
  CheckCircle,
  TrendingUp,
  MessageCircle,
  Briefcase,
  Target,
  Sparkles,
  BarChart,
  FileText,
}

function getFacetStyle(facetTypeId: string): { category: FacetCategory; color: string; icon: LucideIcon } {
  const config = FACET_TYPE_CONFIG[facetTypeId as FacetType]
  if (config) {
    return {
      category: config.category,
      color: config.color,
      icon: ICON_MAP[config.icon] || FileText,
    }
  }
  return { category: 'insights', color: '#6c757d', icon: MessageCircle }
}

// ============================================================================
// ENTITY TYPES (for dropdown)
// ============================================================================

const ENTITY_TYPES = [
  'Problem',
  'CustomerPain',
  'Opportunity',
  'Decision',
  'Constraint',
  'Risk',
  'Stakeholder',
  'Competitor',
  'Alternative',
  'ActionItem',
  'Requirement',
  'Feature',
  'Goal',
  'Metric',
]

// ============================================================================
// FACET SELECTION CARD
// ============================================================================

interface FacetSelectionCardProps {
  facet: DetectedFacet
  isSelected: boolean
  isInGroup: boolean
  groupColor?: string
  onToggleSelect: () => void
  onDragStart?: (e: React.DragEvent) => void
  isDragging?: boolean
}

function FacetSelectionCard({
  facet,
  isSelected,
  isInGroup,
  groupColor,
  onToggleSelect,
  onDragStart,
  isDragging,
}: FacetSelectionCardProps) {
  const style = getFacetStyle(facet.facetTypeId)
  const Icon = style.icon
  const facetInfo = FACET_MAP.get(facet.facetTypeId)

  return (
    <Card
      className={cn(
        'p-2 cursor-pointer transition-all border relative',
        isSelected && !isInGroup && 'ring-2 ring-solution bg-solution/5',
        isInGroup && 'opacity-60 bg-warm-100',
        isDragging && 'opacity-50 scale-95',
        !isSelected && !isInGroup && 'hover:bg-warm-50'
      )}
      style={{
        borderColor: isInGroup && groupColor ? groupColor : (isSelected ? style.color : 'transparent'),
        borderLeftWidth: isInGroup ? '3px' : '1px',
        borderLeftColor: groupColor || 'transparent',
      }}
      draggable={isSelected && !isInGroup}
      onDragStart={onDragStart}
      onClick={onToggleSelect}
    >
      {/* Drag Handle */}
      {isSelected && !isInGroup && (
        <div className="absolute left-0 top-0 bottom-0 w-6 flex items-center justify-center cursor-grab text-text-muted hover:text-text-secondary">
          <GripVertical className="w-3 h-3" />
        </div>
      )}

      <div className={cn('flex items-start gap-2', isSelected && !isInGroup && 'ml-4')}>
        {/* Selection indicator */}
        <div
          className={cn(
            'w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5',
            isSelected ? 'bg-solution border-solution' : 'border-warm-300'
          )}
        >
          {isSelected && <Check className="w-3 h-3 text-white" />}
        </div>

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            <div
              className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${style.color}20` }}
            >
              <Icon className="w-2.5 h-2.5" style={{ color: style.color }} />
            </div>
            <Badge
              variant="outline"
              className="text-[10px] px-1 py-0"
              style={{ borderColor: style.color, color: style.color }}
            >
              {facetInfo?.name || facet.facetTypeId.replace(/_/g, ' ')}
            </Badge>
            <span className="text-[10px] text-text-muted">
              {Math.round(facet.confidence * 100)}%
            </span>
            {isInGroup && (
              <Badge variant="secondary" className="text-[10px] px-1 py-0">
                In Group
              </Badge>
            )}
          </div>

          {/* Content preview */}
          <p className="text-xs line-clamp-2">{facet.content}</p>
        </div>
      </div>
    </Card>
  )
}

// ============================================================================
// FACET SELECTION PANEL (Left Side)
// ============================================================================

interface FacetSelectionPanelProps {
  facets: DetectedFacet[]
  selections: Map<string, FacetSelection>
  onToggleSelect: (facetId: string) => void
  onSelectAll: (category: FacetCategory) => void
  onDeselectAll: (category: FacetCategory) => void
  onDragStart: (facetId: string, e: React.DragEvent) => void
  draggingFacetId: string | null
  groupAssignments: Map<string, { groupId: string; color: string }>
}

function FacetSelectionPanel({
  facets,
  selections,
  onToggleSelect,
  onSelectAll,
  onDeselectAll,
  onDragStart,
  draggingFacetId,
  groupAssignments,
}: FacetSelectionPanelProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<FacetCategory>>(
    new Set(['market', 'business', 'solution', 'insights'])
  )

  // Group facets by category
  const facetsByCategory = useMemo(() => {
    const grouped: Record<FacetCategory, DetectedFacet[]> = {
      market: [],
      business: [],
      solution: [],
      insights: [],
    }
    facets.forEach(facet => {
      const style = getFacetStyle(facet.facetTypeId)
      grouped[style.category].push(facet)
    })
    return grouped
  }, [facets])

  const toggleCategory = (category: FacetCategory) => {
    setExpandedCategories(prev => {
      const next = new Set(prev)
      if (next.has(category)) {
        next.delete(category)
      } else {
        next.add(category)
      }
      return next
    })
  }

  // Count selected in each category
  const getCategoryStats = (category: FacetCategory) => {
    const categoryFacets = facetsByCategory[category]
    const selected = categoryFacets.filter(f => selections.get(f.id)?.selected).length
    const inGroups = categoryFacets.filter(f => groupAssignments.has(f.id)).length
    return { total: categoryFacets.length, selected, inGroups }
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-shrink-0 p-3 border-b border-warm-200 bg-warm-50/50">
        <h3 className="font-semibold flex items-center gap-2">
          <Layers className="w-4 h-4 text-solution" />
          Detected Facets
        </h3>
        <p className="text-xs text-text-muted mt-1">
          Select facets to create entities. Drag to group related facets.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto">
        {(Object.entries(FACET_CATEGORY_CONFIG) as [FacetCategory, typeof FACET_CATEGORY_CONFIG[FacetCategory]][]).map(([category, config]) => {
          const categoryFacets = facetsByCategory[category]
          if (categoryFacets.length === 0) return null

          const CategoryIcon = CATEGORY_ICONS[category]
          const isExpanded = expandedCategories.has(category)
          const stats = getCategoryStats(category)

          return (
            <div key={category} className="border-b border-warm-200">
              {/* Category Header */}
              <div
                className="flex items-center gap-2 p-2 cursor-pointer hover:bg-warm-50 transition-colors"
                onClick={() => toggleCategory(category)}
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-text-muted" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-text-muted" />
                )}
                <div
                  className="w-5 h-5 rounded flex items-center justify-center"
                  style={{ backgroundColor: `${config.color}20` }}
                >
                  <CategoryIcon className="w-3 h-3" style={{ color: config.color }} />
                </div>
                <span className="text-sm font-medium flex-1" style={{ color: config.color }}>
                  {config.label}
                </span>
                <Badge variant="secondary" className="text-xs">
                  {stats.selected}/{stats.total}
                </Badge>
              </div>

              {/* Category Actions & Content */}
              {isExpanded && (
                <div className="px-2 pb-2">
                  {/* Quick actions */}
                  <div className="flex gap-2 mb-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs px-2"
                      onClick={(e) => {
                        e.stopPropagation()
                        onSelectAll(category)
                      }}
                    >
                      Select All
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs px-2"
                      onClick={(e) => {
                        e.stopPropagation()
                        onDeselectAll(category)
                      }}
                    >
                      Clear
                    </Button>
                  </div>

                  {/* Facets */}
                  <div className="space-y-1.5">
                    {categoryFacets.map(facet => {
                      const selection = selections.get(facet.id)
                      const groupInfo = groupAssignments.get(facet.id)
                      return (
                        <FacetSelectionCard
                          key={facet.id}
                          facet={facet}
                          isSelected={selection?.selected || false}
                          isInGroup={!!groupInfo}
                          groupColor={groupInfo?.color}
                          onToggleSelect={() => onToggleSelect(facet.id)}
                          onDragStart={(e) => onDragStart(facet.id, e)}
                          isDragging={draggingFacetId === facet.id}
                        />
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ============================================================================
// GROUPING PANEL (Right Side)
// ============================================================================

interface GroupingPanelProps {
  groups: DraftGroup[]
  facets: DetectedFacet[]
  onCreateGroup: () => void
  onUpdateGroup: (groupId: string, updates: Partial<DraftGroup>) => void
  onDeleteGroup: (groupId: string) => void
  onRemoveFacetFromGroup: (groupId: string, facetId: string) => void
  onSetPrimaryFacet: (groupId: string, facetId: string) => void
  onDragOver: (e: React.DragEvent) => void
  onDropOnGroup: (groupId: string, e: React.DragEvent) => void
  onDropToCreateGroup: (e: React.DragEvent) => void
  selectedFacetIds: string[]
  onConfirmGroup: (groupId: string) => void
  confirmingGroupId: string | null
}

const GROUP_COLORS = [
  '#00b894', // market green
  '#4a90a4', // business blue
  '#d4a020', // solution gold
  '#9b59b6', // purple
  '#e67e22', // orange
  '#1abc9c', // teal
  '#e74c3c', // red
  '#3498db', // light blue
]

function GroupingPanel({
  groups,
  facets,
  onCreateGroup,
  onUpdateGroup,
  onDeleteGroup,
  onRemoveFacetFromGroup,
  onSetPrimaryFacet,
  onDragOver,
  onDropOnGroup,
  onDropToCreateGroup,
  selectedFacetIds,
  onConfirmGroup,
  confirmingGroupId,
}: GroupingPanelProps) {
  const getFacetById = useCallback((id: string) => facets.find(f => f.id === id), [facets])

  const getGroupColor = (index: number) => GROUP_COLORS[index % GROUP_COLORS.length]

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-shrink-0 p-3 border-b border-warm-200 bg-warm-50/50">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold flex items-center gap-2">
              <Users className="w-4 h-4 text-solution" />
              Entity Groups
            </h3>
            <p className="text-xs text-text-muted mt-1">
              Group related facets into single entities
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="gap-1 h-7"
            onClick={onCreateGroup}
            disabled={selectedFacetIds.length === 0}
          >
            <Plus className="w-3 h-3" />
            New Group
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        {groups.length === 0 ? (
          <div
            className="border-2 border-dashed border-warm-300 rounded-lg p-6 text-center"
            onDragOver={onDragOver}
            onDrop={onDropToCreateGroup}
          >
            <Layers className="w-8 h-8 text-text-muted mx-auto mb-2" />
            <p className="text-sm text-text-muted">
              Drag selected facets here to create a group
            </p>
            <p className="text-xs text-text-muted mt-1">
              Or select facets and click "New Group"
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((group, index) => {
              const groupColor = getGroupColor(index)
              const groupFacets = group.facetIds.map(getFacetById).filter(Boolean) as DetectedFacet[]

              return (
                <Card
                  key={group.id}
                  className="p-3 border-l-4"
                  style={{ borderLeftColor: groupColor }}
                  onDragOver={onDragOver}
                  onDrop={(e) => onDropOnGroup(group.id, e)}
                >
                  {/* Group Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <Input
                        value={group.entityLabel}
                        onChange={(e) => onUpdateGroup(group.id, { entityLabel: e.target.value })}
                        placeholder="Entity label..."
                        className="h-8 text-sm font-medium mb-2"
                      />
                      <div className="flex gap-2">
                        <Select
                          value={group.entityType}
                          onValueChange={(v) => onUpdateGroup(group.id, { entityType: v })}
                        >
                          <SelectTrigger className="h-7 w-36 text-xs">
                            <SelectValue placeholder="Entity type" />
                          </SelectTrigger>
                          <SelectContent>
                            {ENTITY_TYPES.map(type => (
                              <SelectItem key={type} value={type} className="text-xs">
                                {type}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-text-muted hover:text-red-500"
                      onClick={() => onDeleteGroup(group.id)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>

                  {/* Description */}
                  <Textarea
                    value={group.entityDescription}
                    onChange={(e) => onUpdateGroup(group.id, { entityDescription: e.target.value })}
                    placeholder="Entity description (optional)..."
                    className="text-xs h-16 resize-none mb-3"
                  />

                  {/* Facets in Group */}
                  <div className="space-y-1.5">
                    <Label className="text-xs text-text-muted">
                      Facets ({groupFacets.length})
                    </Label>
                    {groupFacets.map(facet => {
                      const style = getFacetStyle(facet.facetTypeId)
                      const Icon = style.icon
                      const isPrimary = group.primaryFacetId === facet.id

                      return (
                        <div
                          key={facet.id}
                          className={cn(
                            'flex items-start gap-2 p-2 rounded text-xs bg-warm-50',
                            isPrimary && 'ring-1 ring-solution'
                          )}
                        >
                          <div
                            className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${style.color}20` }}
                          >
                            <Icon className="w-2.5 h-2.5" style={{ color: style.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="line-clamp-2">{facet.content}</p>
                            {isPrimary && (
                              <Badge variant="outline" className="text-[10px] mt-1 border-solution text-solution">
                                Primary
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {!isPrimary && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-5 w-5 p-0 text-text-muted hover:text-solution"
                                onClick={() => onSetPrimaryFacet(group.id, facet.id)}
                                title="Set as primary"
                              >
                                <Sparkles className="w-3 h-3" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0 text-text-muted hover:text-red-500"
                              onClick={() => onRemoveFacetFromGroup(group.id, facet.id)}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {/* Drop zone indicator */}
                  <div className="mt-2 border border-dashed border-warm-300 rounded p-2 text-center text-[10px] text-text-muted">
                    Drag facets here to add
                  </div>

                  {/* Confirm button */}
                  <Button
                    size="sm"
                    className="w-full mt-3 gap-1"
                    disabled={!group.entityLabel || !group.entityType || groupFacets.length === 0 || confirmingGroupId === group.id}
                    onClick={() => onConfirmGroup(group.id)}
                  >
                    {confirmingGroupId === group.id ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-3 h-3" />
                        Create Entity
                      </>
                    )}
                  </Button>
                </Card>
              )
            })}

            {/* Drop zone for new group */}
            <div
              className="border-2 border-dashed border-warm-300 rounded-lg p-4 text-center"
              onDragOver={onDragOver}
              onDrop={onDropToCreateGroup}
            >
              <Plus className="w-5 h-5 text-text-muted mx-auto mb-1" />
              <p className="text-xs text-text-muted">Drop to create new group</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// STANDALONE FACETS PANEL (Bottom)
// ============================================================================

interface StandaloneFacetsPanelProps {
  facets: DetectedFacet[]
  selectedIds: string[]
  onCreateEntities: () => void
  isCreating: boolean
}

function StandaloneFacetsPanel({
  facets,
  selectedIds,
  onCreateEntities,
  isCreating,
}: StandaloneFacetsPanelProps) {
  const selectedFacets = facets.filter(f => selectedIds.includes(f.id))

  if (selectedFacets.length === 0) return null

  return (
    <div className="flex-shrink-0 border-t border-warm-200 bg-warm-50/50 p-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">
            {selectedFacets.length} standalone facet{selectedFacets.length !== 1 ? 's' : ''} selected
          </p>
          <p className="text-xs text-text-muted">
            These will create individual entities (1:1 mapping)
          </p>
        </div>
        <Button
          onClick={onCreateEntities}
          disabled={isCreating || selectedFacets.length === 0}
          className="gap-2"
        >
          {isCreating ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Creating...
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              Create {selectedFacets.length} Entities
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface ReviewBoardViewProps {
  sourceId: string
  onBack: () => void
  onComplete: () => void
}

export function ReviewBoardView({ sourceId, onBack, onComplete }: ReviewBoardViewProps) {
  const { accountId, projectId } = useSessionStore()
  const [source, setSource] = useState<SourceWithFacets | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Selection state
  const [selections, setSelections] = useState<Map<string, FacetSelection>>(new Map())

  // Groups state
  const [draftGroups, setDraftGroups] = useState<DraftGroup[]>([])
  const [savedGroups, setSavedGroups] = useState<FacetEntityGroupWithMembers[]>([])
  const [confirmingGroupId, setConfirmingGroupId] = useState<string | null>(null)

  // Drag state
  const [draggingFacetId, setDraggingFacetId] = useState<string | null>(null)

  // Creating state
  const [creatingStandalone, setCreatingStandalone] = useState(false)

  // Load source and groups
  useEffect(() => {
    if (!accountId || !projectId || !sourceId) return

    const loadData = async () => {
      try {
        setLoading(true)
        setError(null)

        const [sourceResult, groupsResult] = await Promise.all([
          sourcesApi.get(accountId, projectId, sourceId),
          sourcesApi.listGroups(accountId, projectId, sourceId),
        ])

        setSource(sourceResult)
        setSavedGroups(groupsResult)

        // Initialize selections - all pending facets are pre-selected
        const initialSelections = new Map<string, FacetSelection>()
        sourceResult.facets.forEach(facet => {
          // Check if facet is already in a saved group
          const inGroup = groupsResult.some(g =>
            g.members.some(m => m.facetId === facet.id)
          )
          initialSelections.set(facet.id, {
            id: facet.id,
            selected: facet.status === 'pending' && !inGroup,
            groupId: inGroup ? groupsResult.find(g => g.members.some(m => m.facetId === facet.id))?.id : undefined,
          })
        })
        setSelections(initialSelections)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [accountId, projectId, sourceId])

  // Compute group assignments (for coloring facets in groups)
  const groupAssignments = useMemo(() => {
    const assignments = new Map<string, { groupId: string; color: string }>()

    // From draft groups
    draftGroups.forEach((group, index) => {
      const color = GROUP_COLORS[index % GROUP_COLORS.length]
      group.facetIds.forEach(facetId => {
        assignments.set(facetId, { groupId: group.id, color })
      })
    })

    // From saved groups
    savedGroups.forEach((group, index) => {
      const colorIndex = draftGroups.length + index
      const color = GROUP_COLORS[colorIndex % GROUP_COLORS.length]
      group.members.forEach(member => {
        assignments.set(member.facetId, { groupId: group.id, color })
      })
    })

    return assignments
  }, [draftGroups, savedGroups])

  // Get selected facet IDs that are not in groups
  const selectedStandaloneFacetIds = useMemo(() => {
    const ids: string[] = []
    selections.forEach((sel, id) => {
      if (sel.selected && !groupAssignments.has(id)) {
        ids.push(id)
      }
    })
    return ids
  }, [selections, groupAssignments])

  // Handlers
  const handleToggleSelect = useCallback((facetId: string) => {
    setSelections(prev => {
      const next = new Map(prev)
      const current = next.get(facetId)
      if (current) {
        next.set(facetId, { ...current, selected: !current.selected })
      }
      return next
    })
  }, [])

  const handleSelectAllInCategory = useCallback((category: FacetCategory) => {
    if (!source) return
    setSelections(prev => {
      const next = new Map(prev)
      source.facets.forEach(facet => {
        const style = getFacetStyle(facet.facetTypeId)
        if (style.category === category && !groupAssignments.has(facet.id)) {
          const current = next.get(facet.id)
          if (current) {
            next.set(facet.id, { ...current, selected: true })
          }
        }
      })
      return next
    })
  }, [source, groupAssignments])

  const handleDeselectAllInCategory = useCallback((category: FacetCategory) => {
    if (!source) return
    setSelections(prev => {
      const next = new Map(prev)
      source.facets.forEach(facet => {
        const style = getFacetStyle(facet.facetTypeId)
        if (style.category === category) {
          const current = next.get(facet.id)
          if (current) {
            next.set(facet.id, { ...current, selected: false })
          }
        }
      })
      return next
    })
  }, [source])

  const handleDragStart = useCallback((facetId: string, e: React.DragEvent) => {
    setDraggingFacetId(facetId)
    e.dataTransfer.setData('text/plain', facetId)
    e.dataTransfer.effectAllowed = 'move'
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }, [])

  const handleCreateGroup = useCallback(() => {
    const newGroup: DraftGroup = {
      id: `draft-${Date.now()}`,
      entityLabel: '',
      entityType: '',
      entityDescription: '',
      facetIds: [...selectedStandaloneFacetIds],
      primaryFacetId: selectedStandaloneFacetIds[0],
    }

    setDraftGroups(prev => [...prev, newGroup])

    // Clear selections for facets added to group
    setSelections(prev => {
      const next = new Map(prev)
      selectedStandaloneFacetIds.forEach(id => {
        const current = next.get(id)
        if (current) {
          next.set(id, { ...current, selected: false })
        }
      })
      return next
    })
  }, [selectedStandaloneFacetIds])

  const handleDropToCreateGroup = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    const facetId = e.dataTransfer.getData('text/plain')
    if (!facetId) return

    // Get all selected facets including the dragged one
    const facetIds = selectedStandaloneFacetIds.includes(facetId)
      ? selectedStandaloneFacetIds
      : [facetId]

    const newGroup: DraftGroup = {
      id: `draft-${Date.now()}`,
      entityLabel: '',
      entityType: '',
      entityDescription: '',
      facetIds,
      primaryFacetId: facetId,
    }

    setDraftGroups(prev => [...prev, newGroup])
    setDraggingFacetId(null)

    // Clear selections
    setSelections(prev => {
      const next = new Map(prev)
      facetIds.forEach(id => {
        const current = next.get(id)
        if (current) {
          next.set(id, { ...current, selected: false })
        }
      })
      return next
    })
  }, [selectedStandaloneFacetIds])

  const handleDropOnGroup = useCallback((groupId: string, e: React.DragEvent) => {
    e.preventDefault()
    const facetId = e.dataTransfer.getData('text/plain')
    if (!facetId) return

    // Get all selected facets including the dragged one
    const facetIds = selectedStandaloneFacetIds.includes(facetId)
      ? selectedStandaloneFacetIds
      : [facetId]

    setDraftGroups(prev =>
      prev.map(g =>
        g.id === groupId
          ? { ...g, facetIds: [...new Set([...g.facetIds, ...facetIds])] }
          : g
      )
    )
    setDraggingFacetId(null)

    // Clear selections
    setSelections(prev => {
      const next = new Map(prev)
      facetIds.forEach(id => {
        const current = next.get(id)
        if (current) {
          next.set(id, { ...current, selected: false })
        }
      })
      return next
    })
  }, [selectedStandaloneFacetIds])

  const handleUpdateGroup = useCallback((groupId: string, updates: Partial<DraftGroup>) => {
    setDraftGroups(prev =>
      prev.map(g => (g.id === groupId ? { ...g, ...updates } : g))
    )
  }, [])

  const handleDeleteGroup = useCallback((groupId: string) => {
    const group = draftGroups.find(g => g.id === groupId)
    if (!group) return

    // Return facets to selection
    setSelections(prev => {
      const next = new Map(prev)
      group.facetIds.forEach(id => {
        const current = next.get(id)
        if (current) {
          next.set(id, { ...current, selected: true })
        }
      })
      return next
    })

    setDraftGroups(prev => prev.filter(g => g.id !== groupId))
  }, [draftGroups])

  const handleRemoveFacetFromGroup = useCallback((groupId: string, facetId: string) => {
    setDraftGroups(prev =>
      prev.map(g =>
        g.id === groupId
          ? {
              ...g,
              facetIds: g.facetIds.filter(id => id !== facetId),
              primaryFacetId: g.primaryFacetId === facetId ? g.facetIds[0] : g.primaryFacetId,
            }
          : g
      )
    )

    // Return facet to selection
    setSelections(prev => {
      const next = new Map(prev)
      const current = next.get(facetId)
      if (current) {
        next.set(facetId, { ...current, selected: true })
      }
      return next
    })
  }, [])

  const handleSetPrimaryFacet = useCallback((groupId: string, facetId: string) => {
    setDraftGroups(prev =>
      prev.map(g => (g.id === groupId ? { ...g, primaryFacetId: facetId } : g))
    )
  }, [])

  const handleConfirmGroup = useCallback(async (groupId: string) => {
    if (!accountId || !projectId || !source) return

    const group = draftGroups.find(g => g.id === groupId)
    if (!group) return

    try {
      setConfirmingGroupId(groupId)
      setError(null)

      // Create the group via API
      const result = await sourcesApi.createGroup(accountId, projectId, source.id, {
        entityLabel: group.entityLabel,
        entityType: group.entityType,
        entityDescription: group.entityDescription || undefined,
        facetIds: group.facetIds,
        primaryFacetId: group.primaryFacetId,
      })

      // Create entity from group
      await sourcesApi.createEntityFromGroup(accountId, projectId, source.id, result.id)

      // Remove from draft groups
      setDraftGroups(prev => prev.filter(g => g.id !== groupId))

      // Reload data
      const [sourceResult, groupsResult] = await Promise.all([
        sourcesApi.get(accountId, projectId, source.id),
        sourcesApi.listGroups(accountId, projectId, source.id),
      ])

      setSource(sourceResult)
      setSavedGroups(groupsResult)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create entity group')
    } finally {
      setConfirmingGroupId(null)
    }
  }, [accountId, projectId, source, draftGroups])

  const handleCreateStandaloneEntities = useCallback(async () => {
    if (!accountId || !projectId || !source) return

    try {
      setCreatingStandalone(true)
      setError(null)

      // First accept all selected facets
      await sourcesApi.bulkUpdateFacets(
        accountId,
        projectId,
        source.id,
        selectedStandaloneFacetIds,
        'accept'
      )

      // Then create entities
      await sourcesApi.bulkCreateEntities(
        accountId,
        projectId,
        source.id,
        selectedStandaloneFacetIds
      )

      // Reload source
      const updated = await sourcesApi.get(accountId, projectId, source.id)
      setSource(updated)

      // Clear selections
      setSelections(prev => {
        const next = new Map(prev)
        selectedStandaloneFacetIds.forEach(id => {
          const current = next.get(id)
          if (current) {
            next.set(id, { ...current, selected: false })
          }
        })
        return next
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create entities')
    } finally {
      setCreatingStandalone(false)
    }
  }, [accountId, projectId, source, selectedStandaloneFacetIds])

  // Loading state
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-solution" />
      </div>
    )
  }

  // Error state
  if (error && !source) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={onBack} className="mb-4 gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <div className="bg-red-50 text-red-700 p-4 rounded-lg border border-red-200">
          {error}
        </div>
      </div>
    )
  }

  if (!source) return null

  const pendingFacets = source.facets.filter(f => f.status === 'pending' || f.status === 'accepted')

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-warm-200 bg-warm-50/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={onBack} className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-solution" />
                Review Board
              </h1>
              <p className="text-text-muted text-sm">
                {source.title} · {pendingFacets.length} facets to review
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={onComplete} className="gap-2">
              <CheckCircle className="w-4 h-4" />
              Done
            </Button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mt-3 bg-red-50 text-red-700 p-3 rounded-lg text-sm border border-red-200">
            {error}
          </div>
        )}
      </div>

      {/* Main Content - Two Panels */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Facet Selection */}
        <div className="w-1/2 border-r border-warm-200 overflow-hidden">
          <FacetSelectionPanel
            facets={pendingFacets}
            selections={selections}
            onToggleSelect={handleToggleSelect}
            onSelectAll={handleSelectAllInCategory}
            onDeselectAll={handleDeselectAllInCategory}
            onDragStart={handleDragStart}
            draggingFacetId={draggingFacetId}
            groupAssignments={groupAssignments}
          />
        </div>

        {/* Right Panel - Grouping */}
        <div className="w-1/2 overflow-hidden">
          <GroupingPanel
            groups={draftGroups}
            facets={source.facets}
            onCreateGroup={handleCreateGroup}
            onUpdateGroup={handleUpdateGroup}
            onDeleteGroup={handleDeleteGroup}
            onRemoveFacetFromGroup={handleRemoveFacetFromGroup}
            onSetPrimaryFacet={handleSetPrimaryFacet}
            onDragOver={handleDragOver}
            onDropOnGroup={handleDropOnGroup}
            onDropToCreateGroup={handleDropToCreateGroup}
            selectedFacetIds={selectedStandaloneFacetIds}
            onConfirmGroup={handleConfirmGroup}
            confirmingGroupId={confirmingGroupId}
          />
        </div>
      </div>

      {/* Bottom Panel - Standalone Facets Action */}
      <StandaloneFacetsPanel
        facets={source.facets}
        selectedIds={selectedStandaloneFacetIds}
        onCreateEntities={handleCreateStandaloneEntities}
        isCreating={creatingStandalone}
      />
    </div>
  )
}
